import { ChangeEvent, ClipboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Collapse, Fade, Link, Stack, TextField, Typography } from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Timeout } from "react-number-format/types/types";
import { signOut, fetchAuthSession, getCurrentUser } from "aws-amplify/auth";

import { RootState } from "@store/store";
import { Loader } from "@components/Loader";
import { AppButton } from "@components/v2/AppButton";
import { logout, setSignInStep, verifiedOTP } from "@store/slices/sessionSlice";
import { usePostOTPMutation, usePostValidOTPMutation } from "@services/api/security";
import { useLazyGetCurrentUserInfoV2Query, usePatchUserMutation } from "@services/api/users";

import { Mail } from "lucide-react";

import { LoginSwitch } from "./LoginSwitch";
import { otpBoxSx } from "./loginStyles";

interface FormularioMFAProps {
    // email: string;
}

export const FormularioMFA = (props: FormularioMFAProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const hasFetched = useRef(false);

    const inputRefs = [
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
    ];

    const [intervalId, setIntervalId] = useState<Timeout>();
    const [resendTimeout, setResendTimeout] = useState<number>(120);

    const [error, setError] = useState<boolean>(false);
    const [redirected, setRedirected] = useState<boolean>(false);
    const [errroMsg, setErrorMsg] = useState<ReactNode>(undefined);

    const [keepSessionActive, setKeepSessionActive] = useState<boolean>(false);

    const sessionInfo = useSelector((state: RootState) => state.session);

    const [triggerPostOTP] = usePostOTPMutation();
    const [triggerPatchUser] = usePatchUserMutation();
    const [triggerPostValidOTP] = usePostValidOTPMutation();
    const [triggerGetCurrentUserInfo] = useLazyGetCurrentUserInfoV2Query();

    const formik = useFormik({
        initialValues: {
            code1: "",
            code2: "",
            code3: "",
            code4: "",
            code5: "",
            code6: "",
            persistSession: false,
        },
        validationSchema: yup.object().shape({
            code1: yup.number().required("Requerido"),
            code2: yup.number().required("Requerido"),
            code3: yup.number().required("Requerido"),
            code4: yup.number().required("Requerido"),
            code5: yup.number().required("Requerido"),
            code6: yup.number().required("Requerido"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                setError(false);
                setErrorMsg(undefined);

                const result = Object.values(values).reduce((pre, curr) => pre + Number(curr), 0);

                if (import.meta.env.PROD) {
                    await triggerPostValidOTP({
                        code:
                            values.code1.toString() +
                            values.code2.toString() +
                            values.code3.toString() +
                            values.code4.toString() +
                            values.code5.toString() +
                            values.code6.toString(),
                        codeType: "login",
                    }).unwrap();
                } else {
                    if (result !== 21) {
                        throw { status: 400, message: "Código Incorrecto" };
                    }
                }

                const currentUser = await getCurrentUser();
                const userInfo = await triggerGetCurrentUserInfo(currentUser.userId).unwrap();

                await triggerPatchUser({
                    userId: userInfo.data.user_data.id,
                    body: {
                        preferences: {
                            ...userInfo.data.user_data.preferences,
                            sessionOptions: {
                                ...userInfo.data.user_data.preferences.sessionOptions,
                                persist: keepSessionActive,
                            },
                        },
                    },
                }).unwrap();

                setRedirected(true);
                setTimeout(
                    (userInfo: any) => {
                        dispatch(verifiedOTP());
                        dispatch(setSignInStep("WELCOME"));

                        if (
                            Object.keys(userInfo.data.user_data.preferences.sessionOptions).includes(
                                "defaultVersion",
                            ) &&
                            userInfo.data.user_data.preferences.sessionOptions.defaultVersion == "v2"
                        ) {
                            navigate("/v2/solicitudes");
                        } else {
                            navigate("/solicitudes");
                        }
                    },
                    2000,
                    userInfo,
                );
            } catch (error: any) {
                if (error.status === 400) {
                    setError(true);
                    setErrorMsg("Código incorrecto");
                    formik.setErrors({
                        code1: "a",
                        code2: "a",
                        code3: "s",
                        code4: "a",
                        code5: "a",
                        code6: "a",
                    });
                }

                if (error.status === 404) {
                    if (error.data.data.attemps === 3) {
                        await signOut();
                        dispatch(logout());
                        dispatch(setSignInStep("BLOCKED"));
                    } else {
                        setError(true);
                        setErrorMsg(error.data.message);
                        formik.setErrors({
                            code1: "a",
                            code2: "a",
                            code3: "s",
                            code4: "a",
                            code5: "a",
                            code6: "a",
                        });
                    }
                }

                setTimeout(() => {
                    setErrorMsg(undefined);
                }, 2500);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const hasFormikErrors = useMemo(() => {
        return Object.keys(formik.errors).length > 0;
    }, [formik.errors, formik.touched]);

    const handlesetKeepSessionActiveChanged = (checked: boolean) => {
        setKeepSessionActive(checked);
    };

    const restartTimer = () => {
        clearInterval(intervalId);
        const interId = setInterval(() => {
            setResendTimeout((oldValue) => oldValue - 1);
        }, 1000);

        setIntervalId(interId);
    };

    const handleResendCodeClicked = async (e: any) => {
        try {
            e.preventDefault();
            setResendTimeout(120);
            await triggerPostOTP("login").unwrap();
            restartTimer();
        } catch (error: any) {
            setError(true);
            setErrorMsg(error.data.message);

            setTimeout(() => {
                setErrorMsg(undefined);
            }, 2500);
        }
    };

    const handleKeyDownCode = (event: any, index: number) => {
        if (event.key === "Backspace" && formik.values[event.target.name].length === 0) {
            inputRefs[index - 1]?.current?.focus();
        }
    };

    const handlePasteCode = async (event: ClipboardEvent, index: number) => {
        event.preventDefault();
        const digits = event.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, 6 - index);
        for (let i = 0; i < digits.length; i++) {
            await formik.setFieldValue(`code${index + i + 1}`, digits[i], true);
        }
        const lastFilledIndex = Math.min(index + digits.length - 1, 5);
        inputRefs[lastFilledIndex]?.current?.focus();
        setError(false);
    };

    const handleChangeCode = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
        const name = event.target.name;
        const digitsOnly = event.target.value.replace(/\D/g, "");

        if (event.target.value.length === 1) {
            await formik.setFieldValue(`code${index + 2}`, "", false);
            inputRefs[index + 1]?.current?.focus();
        }

        await formik.setFieldValue(name, digitsOnly, true);
        setError(false);
    };

    const handleLogout = async (e: any) => {
        e.preventDefault();
        await signOut();
        dispatch(logout());
        dispatch(setSignInStep("LOGIN"));
    };

    const requestOTP = async () => {
        try {
            const authSession = await fetchAuthSession();

            if (authSession.tokens === undefined) {
                await signOut();
                dispatch(logout());
                return;
            }

            await triggerPostOTP("login").unwrap();
            setError(false);
            restartTimer();
        } catch (error: any) {
            if (error.status === 429) {
                setError(true);
                setErrorMsg(
                    <Typography component="span" sx={{ alignContent: "center", alignItems: "center" }}>
                        Número máximo de reenvíos de código alcanzado.{" "}
                        <Link component={"button"} onClick={handleLogout} sx={{ mt: -1 }}>
                            Regresar a inicio de sesión
                        </Link>
                    </Typography>,
                );
            } else {
                console.log(error);
                setError(true);
                setErrorMsg("Ocurrio un error al envíar el código OTP.");
            }
        }
    };

    useEffect(() => {
        if (resendTimeout === 0) {
            clearInterval(intervalId);
        }
    }, [resendTimeout]);

    useEffect(() => {
        if (hasFetched.current) return;

        setKeepSessionActive(sessionInfo.user.preferences.sessionOptions.persist);
        requestOTP();

        hasFetched.current = true;
    }, [hasFetched]);

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Stack direction={"column"} sx={{ gap: 2.5 }}>
                {redirected != true && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 56,
                            height: 56,
                            borderRadius: "12px",
                            backgroundColor: "#E3F2F8",
                            color: "secondary.dark",
                            mx: "auto",
                        }}
                    >
                        <Mail size={26} strokeWidth={1.8} />
                    </Box>
                )}
                <Typography
                    sx={{
                        fontFamily: "'Outfit', system-ui, sans-serif",
                        fontSize: "30px",
                        fontWeight: 600,
                        letterSpacing: "-0.02em",
                        color: "text.primary",
                        textAlign: "center",
                    }}
                >
                    {redirected ? "Bienvenido de nuevo" : "¡Hola!"}
                </Typography>

                <Collapse in={redirected} unmountOnExit>
                    <Box
                        sx={{
                            width: "100%",
                            height: "200px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Box>
                            <Loader height="100%" />
                        </Box>
                    </Box>
                </Collapse>
                <Collapse in={!redirected} unmountOnExit>
                    <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column", gap: 2.5 }}>
                        <Box sx={{ textAlign: "center" }}>
                            <Typography sx={{ fontSize: "15px", color: "text.secondary" }}>
                                Ingresa el código que te enviamos vía correo electrónico
                            </Typography>
                        </Box>
                        <Stack
                            direction={"row"}
                            sx={{
                                alignItems: "center",
                                columnGap: 1.25,
                            }}
                        >
                            {["code1", "code2", "code3", "code4", "code5", "code6"].map((code, idx) => (
                                <>
                                    <TextField
                                        key={code}
                                        name={code}
                                        type="text"
                                        inputMode="numeric"
                                        inputRef={inputRefs[idx]}
                                        onKeyDown={(event) => handleKeyDownCode(event, idx)}
                                        onChange={(event) => handleChangeCode(event, idx)}
                                        onPaste={(event) => handlePasteCode(event, idx)}
                                        onBlur={formik.handleBlur}
                                        value={formik.values[code]}
                                        autoComplete="off"
                                        sx={otpBoxSx(error)}
                                        slotProps={{
                                            htmlInput: {
                                                maxLength: 1,
                                                autoComplete: "new-password",
                                                pattern: "[0-9]*",
                                            },
                                        }}
                                    />
                                    {code === "code3" && (
                                        <Typography
                                            sx={{
                                                color: "text.secondary",
                                                fontWeight: 700,
                                            }}
                                        >
                                            -
                                        </Typography>
                                    )}
                                </>
                            ))}
                        </Stack>
                        <Collapse in={error && errroMsg !== undefined} sx={{ width: "100%" }}>
                            <Box
                                sx={{
                                    justifyContent: "center",
                                    display: "flex",
                                }}
                            >
                                <Alert variant="outlined" severity="error" sx={{ textAlign: "center", width: "100%" }}>
                                    {errroMsg}
                                </Alert>
                            </Box>
                        </Collapse>
                        <Typography sx={{ color: "text.secondary", fontWeight: 700 }}>
                            {Math.floor(resendTimeout / 60)
                                .toString()
                                .padStart(2, "0")}
                            :{(resendTimeout % 60).toString().padStart(2, "0")} {resendTimeout < 60 ? "seg" : "min"}.
                        </Typography>

                        <Fade in={resendTimeout === 0} unmountOnExit>
                            <Typography
                                component="button"
                                type="button"
                                onClick={handleResendCodeClicked}
                                sx={{
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: "secondary.main",
                                }}
                            >
                                Reenviar código
                            </Typography>
                        </Fade>
                        <LoginSwitch
                            checked={keepSessionActive}
                            onChange={handlesetKeepSessionActiveChanged}
                            label="Mantener sesión iniciada durante 15 días"
                        />
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "row", gap: 1.5 }}>
                            <AppButton
                                tone="ghost"
                                fullWidth
                                disabled={formik.isSubmitting}
                                onClick={handleLogout}
                                size="large"
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    "&:hover": { backgroundColor: "action.hover", boxShadow: "none" },
                                }}
                            >
                                Regresar
                            </AppButton>
                            <AppButton
                                tone="navy"
                                type="submit"
                                fullWidth
                                disabled={!formik.dirty || hasFormikErrors}
                                loading={formik.isSubmitting}
                                size="large"
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "text.primary",
                                        filter: "brightness(0.92)",
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                Validar código
                            </AppButton>
                        </Box>
                    </Box>
                </Collapse>
            </Stack>
        </form>
    );
};
